import React, { useState } from 'react';
import { Copy, KeyRound, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { teamAccessAPI } from '../lib/api';

const ROLES = [['member', 'Miembro del equipo (sin CRM ni Finanzas)'], ['ceo', 'CEO']];

const LinkResult = ({ res }) => (
  <div className="space-y-2 text-sm" data-testid="team-access-result">
    <p className={res.email_sent ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
      {res.email_sent ? '✓ Email enviado con el enlace para crear la contraseña (válido 48 h).' : 'No se pudo enviar el email. Copia este enlace y mándaselo tú (válido 48 h):'}
    </p>
    {res.setup_link && (
      <div className="flex gap-2">
        <Input readOnly value={res.setup_link} className="text-xs" data-testid="team-access-link" />
        <Button size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(res.setup_link).then(() => toast.success('Copiado'))}><Copy className="w-4 h-4" /></Button>
      </div>
    )}
  </div>
);

// Solo CEO: envía (o reenvía) el enlace para crear contraseña. La contraseña anterior deja de valer.
export function SendAccessButton({ member }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const send = async () => {
    if (!window.confirm(`¿Enviar a ${member.name} el enlace para crear su contraseña? Su contraseña actual dejará de funcionar.`)) return;
    setBusy(true);
    try { setRes(await teamAccessAPI.sendAccess(member.id)); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="ghost" size="sm" className="mt-2 -ml-2 h-8" onClick={send} disabled={busy} data-testid={`send-access-${member.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1" />}Enviar acceso
      </Button>
      <Dialog open={!!res} onOpenChange={(o) => !o && setRes(null)}>
        <DialogContent><DialogHeader><DialogTitle>Acceso de {member.name}</DialogTitle></DialogHeader>{res && <LinkResult res={res} />}</DialogContent>
      </Dialog>
    </>
  );
}

export function InviteMemberButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', role: 'member' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async () => {
    setBusy(true);
    try { setRes(await teamAccessAPI.invite(f)); onDone?.(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const close = () => { setOpen(false); setRes(null); setF({ name: '', email: '', role: 'member' }); };
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="invite-member-btn"><UserPlus className="w-4 h-4 mr-2" />Invitar miembro</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invitar a un miembro del equipo</DialogTitle></DialogHeader>
          {res ? <LinkResult res={res} /> : (
            <div className="space-y-3">
              <div><Label>Nombre *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-member-name" /></div>
              <div><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-member-email" /></div>
              <div><Label>Rol</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger data-testid="invite-member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="w-full" disabled={busy || !f.name.trim() || !f.email.trim()} onClick={submit} data-testid="invite-member-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Crear cuenta y enviar enlace
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
